/**
 * DashboardDefaultRangeCard — workspace-level preference for the
 * default date window the Dashboard opens to.
 *
 * Backed by PATCH /api/workspaces/{id}/settings/dashboard-default-range
 * and read via the matching GET. The Dashboard itself sends
 * `?profit_range=auto` on first load; the backend then checks this
 * setting and, if set to anything other than "auto", honors it instead
 * of the smart-pivot heuristic.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { LayoutDashboard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Order matches the dropdown on the Profit tile so the Settings page
// feels like the same vocabulary the user already sees.
const OPTIONS = [
  { value: 'auto',       label: 'Smart (recommended)',
    hint: "Opens to the current month — but auto-pivots to your most recent activity if there's no data this month. Best for users with historical books." },
  { value: 'mtd',        label: 'Month to date',
    hint: 'Always opens to the current month, even when empty.' },
  { value: 'qtd',        label: 'Quarter to date',
    hint: 'Always opens to the current quarter.' },
  { value: 'ytd',        label: 'Year to date',
    hint: 'Always opens to January 1 of the current year through today.' },
  { value: '7d',         label: 'Last 7 days', hint: 'Rolling 7-day window.' },
  { value: '30d',        label: 'Last 30 days', hint: 'Rolling 30-day window.' },
  { value: '90d',        label: 'Last 90 days', hint: 'Rolling 90-day window.' },
  { value: 'last_month', label: 'Previous full month',
    hint: 'Locks to last month — handy if you close your books on the 5th.' },
];

export default function DashboardDefaultRangeCard({ workspaceId, canWrite = true }) {
  const [value, setValue] = useState('auto');
  const [initial, setInitial] = useState('auto');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await apiFetch(
          `${API_URL}/api/workspaces/${workspaceId}/settings/dashboard-default-range`,
        );
        if (!r.ok) throw new Error('fetch failed');
        const j = await r.json();
        if (!cancelled) {
          setValue(j.dashboard_default_range || 'auto');
          setInitial(j.dashboard_default_range || 'auto');
        }
      } catch {
        // Non-fatal — fall through to the default
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  const dirty = value !== initial;

  const save = async () => {
    if (!workspaceId || !dirty) return;
    setSaving(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/workspaces/${workspaceId}/settings/dashboard-default-range`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dashboard_default_range: value }),
        },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save');
      }
      setInitial(value);
      toast.success('Dashboard default updated');
    } catch (e) {
      toast.error(e.message || 'Could not save dashboard default');
    } finally {
      setSaving(false);
    }
  };

  const currentOption = OPTIONS.find(o => o.value === value) || OPTIONS[0];

  return (
    <Card data-testid="dashboard-default-range-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
            <LayoutDashboard className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <CardTitle>Dashboard default period</CardTitle>
            <CardDescription>
              Pick the date window the Dashboard opens to for everyone in this workspace.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-w-md">
          <Label htmlFor="dashboard-default-range-select">Default period</Label>
          <Select
            value={value}
            onValueChange={setValue}
            disabled={loading || saving || !canWrite}
          >
            <SelectTrigger
              id="dashboard-default-range-select"
              data-testid="dashboard-default-range-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`dashboard-default-range-option-${opt.value}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {currentOption.hint}
          </p>
          <div className="flex items-center gap-2 pt-2">
            <Button
              onClick={save}
              disabled={!dirty || saving || loading || !canWrite}
              data-testid="dashboard-default-range-save"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
            {!canWrite && (
              <p className="text-xs text-muted-foreground">
                Only owners and admins can change this.
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
